// ---------------------------------------------------------------------------
// Derivações puras — convertem dados que vêm do backend (ou podem ser
// derivados deles) em valores prontos para renderização.
//
// Mantêm o protótipo desacoplado de campos que ainda não existem no backend
// (ex: tipo de serviço explícito, volumetria, cor por técnico) e ficam fáceis
// de testar em isolamento.
//
// Cada função é pura: mesmo input → mesmo output, sem efeitos colaterais.
// ---------------------------------------------------------------------------

// ─── Tipo de serviço ───────────────────────────────────────────────────────
// Rótulo curto do card. Lê a seleção services[] (handoff 0039): 0 serviços =
// "Serviço" (genérico/não classificado), 1 = a descrição do serviço, 2+ =
// "Combo". As 4 colunas *_qty legadas saíram (não ler mais — Fatia 7 as removerá).

export function deriveSvc(item) {
   const services = Array.isArray(item.services) ? item.services : [];
   if (services.length === 0) return 'Serviço';
   if (services.length > 1) return 'Combo';
   return services[0]?.service_description || 'Serviço';
}

// ─── Status do stop ────────────────────────────────────────────────────────
// Combina item_status (v0) e o evento ativo mais recente (v1):
//   item terminal                        → 'done'
//   evento ativo mais recente é ON_SERVICE → 'run'
//   demais                                → 'idle'
//
// Backend devolve active_events ordenado por event_timestamp ASC (handoff §6.5).

const TERMINAL_STATUSES = new Set(['REALIZADO', 'NAO_REALIZADO']);

export function deriveStopStatus(item, events = []) {
   if (TERMINAL_STATUSES.has(item.item_status)) return 'done';
   if (!events || events.length === 0) return 'idle';
   const latest = events[events.length - 1];
   if (latest.event_kind === 'ON_SERVICE') return 'run';
   return 'idle';
}

// ─── SLA ───────────────────────────────────────────────────────────────────
// Baseado em customer_window_* e estimated_arrival_time.
//   sem janela ou sem ETA → 'low' (não dá pra inferir)
//   ETA antes do fim da janela com folga ≥ 30min → 'low'
//   ETA dentro mas <30min do fim → 'med'
//   ETA depois do fim da janela → 'high'

function timeStringToMinutes(t) {
   if (!t) return null;
   const [h, m] = String(t).split(':').map(Number);
   if (Number.isNaN(h) || Number.isNaN(m)) return null;
   return h * 60 + m;
}

export function deriveSla(item) {
   const end = timeStringToMinutes(item.customer_window_end);
   const eta = timeStringToMinutes(item.estimated_arrival_time);
   if (end === null || eta === null) return 'low';
   if (eta > end) return 'high';
   if (eta >= end - 30) return 'med';
   return 'low';
}

// ─── Cor por técnico ───────────────────────────────────────────────────────
// Hash determinístico de employee.id mod 5 → 'c1'..'c5' (avatar)
// e cor hex correspondente para a rota no mapa. Mesma ordem do CSS:
//   c1=#B53636, c2=#2563EB, c3=#16A34A, c4=#7C3AED, c5=#D97706.

const ROUTE_COLOR_HEX = ['#B53636', '#2563EB', '#16A34A', '#7C3AED', '#D97706'];

function colorIndex(employeeId) {
   const n = Number(employeeId);
   if (Number.isNaN(n) || !Number.isFinite(n)) return 0;
   return Math.abs(n) % 5;
}

export function deriveColorClass(employeeId) {
   return `c${colorIndex(employeeId) + 1}`;
}

export function deriveRouteColor(employeeId) {
   return ROUTE_COLOR_HEX[colorIndex(employeeId)];
}

// ─── Paleta de rotas (sequencial por ORDEM da rota) ────────────────────────
// 20 cores máximas-distintas, atribuídas em sequência: a 1ª rota recebe a 1ª
// cor, a 2ª recebe a 2ª, etc. (NÃO é hash — é posição). Com >20 rotas, cicla.
// Substitui o esquema antigo de 5 cores por hash (deriveRouteColor), que
// repetia/aproximava tons e dificultava diferenciar rotas no mapa.
export const ROUTE_PALETTE = [
   '#E6194B', // 1  vermelho
   '#3CB44B', // 2  verde
   '#4363D8', // 3  azul
   '#F58231', // 4  laranja
   '#911EB4', // 5  roxo
   '#008080', // 6  teal
   '#F032E6', // 7  magenta
   '#9A6324', // 8  marrom
   '#808000', // 9  oliva
   '#46C2D8', // 10 ciano
   '#BCBD22', // 11 amarelo-esverdeado
   '#E377C2', // 12 rosa
   '#1F77B4', // 13 azul-aço
   '#FF7F0E', // 14 âmbar
   '#2CA02C', // 15 verde-folha
   '#D62728', // 16 vermelho-tijolo
   '#17BECF', // 17 turquesa
   '#7F4FC9', // 18 violeta
   '#8C564B', // 19 castanho
   '#5254A3', // 20 índigo
];

// Cor da rota pela sua posição (0-based) na lista. Cicla com módulo.
export function routeColorByIndex(index) {
   const len = ROUTE_PALETTE.length;
   const i = ((Math.trunc(index) % len) + len) % len;
   return ROUTE_PALETTE[i];
}

// Tinta legível (preto ou branco) sobre um fundo hex — por luminância
// perceptual. Mantém o número da sequência lível tanto em cores escuras
// (#911EB4) quanto claras (#BCBD22) da paleta de rotas.
export function readableInk(hex) {
   if (!hex || typeof hex !== 'string') return '#1F1F18';
   const h = hex.replace('#', '');
   if (h.length < 6) return '#1F1F18';
   const r = parseInt(h.slice(0, 2), 16);
   const g = parseInt(h.slice(2, 4), 16);
   const b = parseInt(h.slice(4, 6), 16);
   const lum = 0.299 * r + 0.587 * g + 0.114 * b;
   return lum > 150 ? '#1F1F18' : '#FFFFFF';
}

// ─── Iniciais do nome ──────────────────────────────────────────────────────
// "Carlos Mendes" → "CM". Pega primeira letra do primeiro nome + primeira
// letra do último nome.

export function deriveInitials(fullName) {
   if (!fullName) return '—';
   const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
   if (parts.length === 0) return '—';
   const first = parts[0][0] || '';
   const last = parts.length > 1 ? parts[parts.length - 1][0] || '' : '';
   const result = (first + last).toUpperCase();
   return result || '—';
}

// ─── Duração estimada (fallback) ───────────────────────────────────────────
// Quando estimated_duration_minutes não vem do backend, derivar do tipo
// de serviço.

const DURATION_BY_KIND = {
   Filtro:        18,
   Higienização:  25,
   Manutenção:    35,
   Instalação:    60,
   Combo:         50,
   Serviço:       30,
};

export function deriveDuration(item) {
   const svc = deriveSvc(item);
   return DURATION_BY_KIND[svc] || 30;
}

// ─── ISO timestamp → "HH:MM" ───────────────────────────────────────────────
// Helper utilizado pelas conversões de eventos para o shape do mock.

function isoToHHMM(iso) {
   if (!iso) return '';
   const date = new Date(iso);
   if (Number.isNaN(date.getTime())) return '';
   const h = String(date.getHours()).padStart(2, '0');
   const m = String(date.getMinutes()).padStart(2, '0');
   return `${h}:${m}`;
}

// "HH:MM" + N minutos → "HH:MM" (estimativa de saída = chegada + duração).
// Devolve null pra entrada inválida (ex.: eta '—'). Faz wrap em 24h.
export function shiftHHMM(hhmm, minutes) {
   if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
   const [h, m] = hhmm.split(':').map(Number);
   if (Number.isNaN(h) || Number.isNaN(m)) return null;
   const total = ((h * 60 + m + (Number(minutes) || 0)) % 1440 + 1440) % 1440;
   const hh = String(Math.floor(total / 60)).padStart(2, '0');
   const mm = String(total % 60).padStart(2, '0');
   return `${hh}:${mm}`;
}

// ─── Eventos → presence[] (shape do StopDrawer) ────────────────────────────
// Filtra ARRIVED/ON_SERVICE/EXECUTED/PARTIAL e converte timestamp em HH:MM.
// OTW é tratado separadamente (deriveOtw).

const PRESENCE_DICT = {
   ARRIVED:     { kind: 'arrived',     label: 'Chegou no local' },
   ON_SERVICE:  { kind: 'on_service',  label: 'Iniciou o serviço' },
   EXECUTED:    { kind: 'executed',    label: 'Completou o serviço com sucesso' },
   PARTIAL:     { kind: 'partial',     label: 'Completou parcialmente o serviço' },
   CANCELLED:   { kind: 'cancelled',   label: 'Não realizado' },
   RESCHEDULED: { kind: 'rescheduled', label: 'Reagendado' },
};

export function derivePresence(events = []) {
   if (!events) return [];
   return events
      .filter((ev) => PRESENCE_DICT[ev.event_kind])
      .map((ev) => ({
         kind: PRESENCE_DICT[ev.event_kind].kind,
         t: isoToHHMM(ev.event_timestamp),
         label: PRESENCE_DICT[ev.event_kind].label,
      }));
}

// ─── Eventos → timeline completa (shape do StopDrawer) ────────────────────
// Diferente de derivePresence (que alimenta as linhas inline do painel e só
// conhece eventos de presença), aqui TODO event_kind vira uma entrada — a
// timeline do drawer mostra o ciclo completo, incluindo CANCELLED corrigido
// por OTW posterior (item 36852 do dev é o caso de teste).

const TIMELINE_LABELS = {
   OTW:             'Técnico a caminho',
   ARRIVED:         'Chegou no local',
   ON_SERVICE:      'Iniciou o serviço',
   EXECUTED:        'Completou o serviço com sucesso',
   PARTIAL:         'Completou parcialmente o serviço',
   CANCELLED:       'Não realizado',
   RESCHEDULED:     'Reagendado',
   // Sintéticos (M2 §3) — gerados pelo backend antes de um terminal marcado
   // "cedo". Chegam com is_synthetic=true e são renderizados com marca "auto".
   ROUTE_SERVICED:  'Rota atendida (auto)',
   ROUTE_CANCELLED: 'Rota cancelada (auto)',
};

// 'YYYY-MM-DD' (ou prefixo de ISO) → 'DD/MM'.
function ymdToDayMonth(ymd) {
   const [, month, day] = String(ymd).split('-');
   return day && month ? `${day}/${month}` : '';
}

// scheduleDate ('YYYY-MM-DD') marca o "dia esperado": eventos registrados em
// outro dia ganham `d` ('DD/MM') pra timeline não parecer fora de ordem —
// ela é sempre cronológica ASC, mas só a hora confundia (ex: evento de hoje
// num agendamento de semanas atrás).
// id/eventKind/isSynthetic seguem adiante (M2): o drawer usa isSynthetic pra
// marca "auto" e id/eventKind pra ligar o botão de remover (LIFO) no último
// evento ativo. eventId é null no mock (timeline derivada de presence/otw).
export function deriveTimeline(events = [], scheduleDate = null) {
   if (!events) return [];
   return events.map((ev) => {
      const eventDate = String(ev.event_timestamp || '').slice(0, 10);
      const isOtherDay = scheduleDate && eventDate && eventDate !== scheduleDate;
      return {
         eventId: ev.id ?? null,
         eventKind: ev.event_kind || null,
         kind: String(ev.event_kind || '').toLowerCase(),
         isSynthetic: Boolean(ev.is_synthetic),
         t: isoToHHMM(ev.event_timestamp),
         d: isOtherDay ? ymdToDayMonth(eventDate) : null,
         label: TIMELINE_LABELS[ev.event_kind] || ev.event_kind,
         notes: ev.notes || null,
      };
   });
}

// ─── Eventos → OTW (último evento OTW) ────────────────────────────────────
// O StopDrawer mostra apenas o OTW mais recente como card "Técnico a caminho".

export function deriveOtw(events = []) {
   if (!events) return null;
   const otws = events.filter((ev) => ev.event_kind === 'OTW');
   if (otws.length === 0) return null;
   const last = otws[otws.length - 1];
   return {
      t: isoToHHMM(last.event_timestamp),
      label: 'Técnico a caminho',
   };
}

// ─── Eventos → balão do mapa (ETAs ancoradas no pino) ─────────────────────
// Resume a cadeia de eventos em 4 marcos pro balão do mapa: trajeto (OTW),
// chegada (ARRIVED), início de serviço (ON_SERVICE) e o desfecho terminal.
// Para cada marco intermediário, devolve o HH:MM do evento mais recente do
// tipo (ou null = "Aguardando..."). O terminal devolve { label, t } com o
// rótulo do desfecho específico, ou null quando ainda não houve.

const BALLOON_TERMINAL_LABELS = {
   EXECUTED:    'Concluído',
   PARTIAL:     'Parcial',
   CANCELLED:   'Não realizado',
   RESCHEDULED: 'Reagendado',
};

export function deriveBalloonEvents(events = []) {
   const list = events || [];
   const latestOf = (kind) => {
      for (let i = list.length - 1; i >= 0; i -= 1) {
         if (list[i].event_kind === kind) {
            return isoToHHMM(list[i].event_timestamp);
         }
      }
      return null;
   };
   let terminal = null;
   for (let i = list.length - 1; i >= 0; i -= 1) {
      const label = BALLOON_TERMINAL_LABELS[list[i].event_kind];
      if (label) {
         terminal = { label, t: isoToHHMM(list[i].event_timestamp) };
         break;
      }
   }
   return {
      otw: latestOf('OTW'),
      arrived: latestOf('ARRIVED'),
      onService: latestOf('ON_SERVICE'),
      terminal,
   };
}

// ─── Motivo de não-roteirização pelo otimizador (handoff 0049) ─────────────
// optimization_skip_reason: o otimizador RECUSOU o item, que ficou no dock. É
// AVISO, não trava — o operador pode arrastar para uma rota mesmo assim. Copy
// PT-BR por enum (texto sugerido no handoff §4); código desconhecido cai no
// genérico, null devolve null (sem tag).

const OPTIMIZATION_SKIP_REASON_LABELS = {
   TIME_WINDOW: 'Não coube no horário marcado',
   CAPACITY:    'Frota sem espaço (lotação / só-carro)',
   NO_VEHICLE:  'Sem veículo disponível',
   ROUTE_LIMIT: 'Rota excederia o limite de duração/distância',
   OTHER:       'Não roteirizado (outro motivo)',
};

export function skipReasonLabel(code) {
   if (!code) return null;
   return OPTIMIZATION_SKIP_REASON_LABELS[code] || OPTIMIZATION_SKIP_REASON_LABELS.OTHER;
}

// ─── Status agregado do técnico ───────────────────────────────────────────
// Derivado dos stops do técnico:
//   tem stop com status='run' → 'run' / 'Em rota'
//   todos done                → 'done' / 'Finalizado'
//   nenhum stop ou todos idle → 'idle' / 'Aguardando'

export function deriveTechStatus(stops) {
   if (!stops || stops.length === 0) {
      return { status: 'idle', label: 'Aguardando' };
   }
   const hasRunning = stops.some((s) => s.status === 'run');
   if (hasRunning) return { status: 'run', label: 'Em rota' };
   const allDone = stops.every((s) => s.status === 'done');
   if (allDone) return { status: 'done', label: 'Finalizado' };
   return { status: 'idle', label: 'Aguardando' };
}

// ─── Bounds + projeção lat/lng → viewBox SVG (1080x720) ───────────────────
// Computa as bounds do conjunto de items que têm coordenadas e projeta cada
// item nesse retângulo, com padding pra evitar pinos colados na borda.
//
// Quando não há nenhum item com lat/lng, computeBounds devolve null e
// latLngToViewBox passa a devolver null — o componente decide como
// renderizar (lista lateral, mapa em fallback).

const VIEWBOX_W = 1080;
const VIEWBOX_H = 720;
const MAP_PADDING = 60;

function hasCoords(item) {
   return (
      item.address_latitude != null &&
      item.address_longitude != null &&
      !Number.isNaN(Number(item.address_latitude)) &&
      !Number.isNaN(Number(item.address_longitude))
   );
}

export function computeBounds(items) {
   if (!items || items.length === 0) return null;
   const points = items.filter(hasCoords).map((it) => ({
      lat: Number(it.address_latitude),
      lng: Number(it.address_longitude),
   }));
   if (points.length === 0) return null;
   const lats = points.map((p) => p.lat);
   const lngs = points.map((p) => p.lng);
   const minLat = Math.min(...lats);
   const maxLat = Math.max(...lats);
   const minLng = Math.min(...lngs);
   const maxLng = Math.max(...lngs);
   // Margem de 5% pra evitar bounds degeneradas (1 só ponto, 2 pontos colados).
   const padLat = (maxLat - minLat) * 0.05 || 0.001;
   const padLng = (maxLng - minLng) * 0.05 || 0.001;
   return {
      minLat: minLat - padLat,
      maxLat: maxLat + padLat,
      minLng: minLng - padLng,
      maxLng: maxLng + padLng,
   };
}

export function latLngToViewBox(item, bounds) {
   if (!bounds || !hasCoords(item)) return null;
   const lat = Number(item.address_latitude);
   const lng = Number(item.address_longitude);
   const dLat = bounds.maxLat - bounds.minLat || 1;
   const dLng = bounds.maxLng - bounds.minLng || 1;
   // x cresce com longitude. y inverte porque o eixo Y do SVG aponta pra baixo.
   const x =
      MAP_PADDING + ((lng - bounds.minLng) / dLng) * (VIEWBOX_W - 2 * MAP_PADDING);
   const y =
      MAP_PADDING + ((bounds.maxLat - lat) / dLat) * (VIEWBOX_H - 2 * MAP_PADDING);
   return { x, y };
}
